// src/components/LandingPage.tsx - Landing page similar to Bear Contact
import React from 'react';
import { Check, Zap, DollarSign, Phone, ChevronRight, AlertTriangle } from 'lucide-react';
import './LandingPage.css';
import icon from '../assets/icon.png';
import { MAINTENANCE_MODE } from '../constants';

interface LandingPageProps {
  onSignIn: () => void;
  onTryForFree: () => void;
  onTerms: () => void;
  onPrivacy: () => void;
  onDataDocs: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignIn, onTryForFree, onTerms, onPrivacy, onDataDocs }) => {
  return (
    <div className={`landing-page${MAINTENANCE_MODE ? ' maintenance-mode' : ''}`}>
      {/* Header */}
      <header className="landing-header">
        <div className="landing-nav">
          {/* Logo */}
          <div className="landing-logo">
            <img src={icon} alt="ppl.contact" className="landing-logo-icon" />
            <span className="landing-logo-text">ppl.contact</span>
          </div>
          
          {/* Navigation buttons */}
          <div className="landing-nav-buttons">
            <button 
              className="landing-btn landing-btn-secondary" 
              onClick={() => window.open('https://calendly.com/joe-ppl/30min', '_blank')}
            >
              Book a Demo
            </button>
            <button
              className="landing-btn landing-btn-secondary"
              onClick={onTryForFree}
              disabled={MAINTENANCE_MODE}
              title={MAINTENANCE_MODE ? 'Temporarily unavailable during maintenance' : undefined}
            >
              Try for Free
            </button>
            <button
              className="landing-btn landing-btn-primary"
              onClick={onSignIn}
              disabled={MAINTENANCE_MODE}
              title={MAINTENANCE_MODE ? 'Temporarily unavailable during maintenance' : undefined}
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {MAINTENANCE_MODE && (
        <div className="landing-maintenance-banner" role="status">
          <AlertTriangle size={18} />
          <span>We are performing maintenance. Sign in is temporarily unavailable.</span>
        </div>
      )}

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-container">
          <div className="landing-hero-content">
            <h1 className="landing-hero-title">
              The right contact data for <span className="landing-hero-title-gradient">growth</span>.
            </h1>
            
            <p className="landing-hero-subtitle">
              Search with our intuitive interface or integrate seamlessly via API.
            </p>
            
            {/* Feature chips */}
            <div className="landing-feature-chips">
              <div className="landing-chip">
                <Check size={16} className="landing-chip-icon" />
                <span>90%+ business email deliverability</span>
              </div>
              <div className="landing-chip">
                <Phone size={16} className="landing-chip-icon" />
                <span>Mobiles included</span>
              </div>
              <div className="landing-chip">
                <DollarSign size={16} className="landing-chip-icon" />
                <span>Pay-as-you-go billing</span>
              </div>
              <div className="landing-chip">
                <Zap size={16} className="landing-chip-icon" />
                <span>Costs 80-90% less than industry average</span>
              </div>
            </div>

            {/* Platform Preview - Real App Style */}
            <div className="landing-platform-preview">
              <div className="landing-preview-card">
                {/* Main App Layout */}
                <div className="landing-preview-app-layout">
                  {/* Sidebar */}
                  <div className="landing-preview-sidebar">
                    {/* Sidebar Header - Match Real App */}
                    <div className="landing-preview-sidebar-header">
                      <button className="landing-preview-back-arrow">‹</button>
                      <div className="landing-preview-segment-control">
                        <button className="landing-preview-segment-button active">Contact</button>
                        <button className="landing-preview-segment-button">Company</button>
                      </div>
                    </div>

                    {/* Filter Content - Match Real App */}
                    <div className="landing-preview-filters">
                      <div className="landing-preview-filter-category">
                        <div className="landing-preview-category-header">
                          <div className="landing-preview-filters-title">Contact Filters</div>
                        </div>
                        <div className="landing-preview-filter-fields">
                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">First Name</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Last Name</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Middle Name</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Job Title</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Department</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Seniority Level</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Headline</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Skills</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Business Email</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Business Email Validation Status</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Business Email Validation Date</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">All Emails</span>
                              </div>
                            </div>
                          </div>

                          <div className="landing-preview-filter-item">
                            <div className="landing-preview-filter-item-header">
                              <div className="landing-preview-filter-field-left">
                                <ChevronRight size={12} className="landing-preview-filter-item-icon" />
                                <span className="landing-preview-filter-item-label">Mobile Phone</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="landing-preview-main">
                    {/* Breadcrumb */}
                    <div className="landing-preview-breadcrumb">
                      <span className="landing-preview-breadcrumb-item">Search</span>
                      <span className="landing-preview-breadcrumb-separator">›</span>
                      <span className="landing-preview-breadcrumb-item">People</span>
                      <span className="landing-preview-result-count">1,247</span>
                    </div>

                    {/* Data Table */}
                    <div className="landing-preview-table-container">
                      <div className="landing-preview-table-wrapper">
                        <table className="landing-preview-table">
                          <thead>
                            <tr className="landing-preview-table-header">
                              <th className="landing-preview-th">Contact</th>
                              <th className="landing-preview-th">Company</th>
                              <th className="landing-preview-th">Location</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">JD</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">John Doe</div>
                                    <div className="landing-preview-contact-title">Chief Executive Officer</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">TC</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">TechCorp</div>
                                    <div className="landing-preview-company-domain">techcorp.com</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">San Francisco, CA, United States</td>
                            </tr>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">SM</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">Sarah Miller</div>
                                    <div className="landing-preview-contact-title">VP of Marketing</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">II</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">InnovateInc</div>
                                    <div className="landing-preview-company-domain">innovateinc.com</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">New York, NY, United States</td>
                            </tr>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">RC</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">Robert Chen</div>
                                    <div className="landing-preview-contact-title">Chief Technology Officer</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">DF</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">DataFlow</div>
                                    <div className="landing-preview-company-domain">dataflow.io</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">Austin, TX, United States</td>
                            </tr>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">EA</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">Emily Anderson</div>
                                    <div className="landing-preview-contact-title">Product Manager</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">CS</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">CloudSync</div>
                                    <div className="landing-preview-company-domain">cloudsync.io</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">Seattle, WA, United States</td>
                            </tr>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">MW</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">Michael Williams</div>
                                    <div className="landing-preview-contact-title">Senior Software Engineer</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">ND</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">NextGen Digital</div>
                                    <div className="landing-preview-company-domain">nextgendigital.com</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">Boston, MA, United States</td>
                            </tr>
                            <tr className="landing-preview-table-row">
                              <td className="landing-preview-td">
                                <div className="landing-preview-contact-cell">
                                  <div className="landing-preview-avatar">LT</div>
                                  <div className="landing-preview-contact-info">
                                    <div className="landing-preview-contact-name">Lisa Thompson</div>
                                    <div className="landing-preview-contact-title">VP of Sales</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">
                                <div className="landing-preview-company-cell">
                                  <div className="landing-preview-company-logo">FT</div>
                                  <div className="landing-preview-company-info">
                                    <div className="landing-preview-company-name">FinTech Solutions</div>
                                    <div className="landing-preview-company-domain">fintechsolutions.com</div>
                                  </div>
                                </div>
                              </td>
                              <td className="landing-preview-td">Chicago, IL, United States</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pagination */}
                    <div className="landing-preview-pagination">
                      <div className="landing-preview-pagination-info">
                        Showing 1-25 of 1,247 results
                      </div>
                      <div className="landing-preview-pagination-controls">
                        <button className="landing-preview-page-btn" disabled>Previous</button>
                        <button className="landing-preview-page-btn active">1</button>
                        <button className="landing-preview-page-btn">2</button>
                        <button className="landing-preview-page-btn">3</button>
                        <button className="landing-preview-page-btn">Next</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer-content">
            <div className="landing-footer-logo">
              <img src={icon} alt="ppl.contact" className="landing-footer-logo-icon" />
              <span className="landing-footer-logo-text">ppl.contact</span>
            </div>
            <div className="landing-footer-links">
              <button className="landing-footer-link" onClick={onTerms}>Terms</button>
              <button className="landing-footer-link" onClick={onPrivacy}>Privacy Policy</button>
              <button className="landing-footer-link" onClick={onDataDocs}>Data Documentation</button>
              <button 
                className="landing-footer-link" 
                onClick={() => window.open('https://api.ppl.contact/redoc', '_blank')}
              >
                API Documentation
              </button>
            </div>
          </div>
          <p className="landing-footer-attribution">
            Built and operated by{' '}
            <a href="https://startwave.io" target="_blank" rel="noopener noreferrer">
              Startwave Technology Inc.
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;


